import { createTheme, ThemeOptions } from "@mui/material/styles";

// Zoulloo brand colors
export const zoullooColors = {
	primary: {
		main: "#FF6300", // Coquelicot (Zoulloo orange from Figma)
		light: "#FF8F70",
		dark: "#E65B2C",
		contrastText: "#FFFFFF"
	},
	secondary: {
		main: "#4836bf", // Persian Blue (Zoulloo brand blue from Figma)
		light: "#c2b8ff",
		dark: "#281E69", // Midnight Blue from Figma
		contrastText: "#FFFFFF",
		alpha: "rgba(72, 54, 191, 0.04)"
	},
	background: {
		default: "#fdfdff",
		paper: "#fdfdff",
		light: "#f5f5f5",
		lighter: "#fafafa",
		lightest: "#f8f9fa",
		grey: "#f9f9f9",
		disabled: "#eaeaea",
		menu: "#EFEEFA"
	},
	text: {
		primary: "#444444",
		secondary: "#666666",
		black: "#000000",
		white: "#ffffff",
		disabled: "#bdbdbd"
	},
	border: {
		main: "rgba(0, 0, 0, 0.23)",
		light: "#e0e0e0",
		lighter: "#e9ecef",
		dashed: "#c4c4c4"
	},
	grey: {
		25: "#f9fafb",
		50: "#FAFAFA",
		75: "#f3f4f6",
		100: "#F3F3F3", // Gris clair from Figma
		150: "#e5e7eb",
		200: "#E3E3E3", // Gris from Figma
		250: "#e0e0e0",
		300: "#E0E0E0",
		400: "#BDBDBD",
		450: "#919191", // Gris moyen from Figma
		500: "#9E9E9E",
		550: "#6c757d",
		575: "#5a6268",
		600: "#757575",
		700: "#616161",
		800: "#424242",
		900: "#2A2A2A" // Gris foncé from Figma
	},
	success: {
		main: "#4CAF50",
		light: "#81C784",
		dark: "#388E3C",
		selected: "#28a745",
		alpha: {
			light: "rgba(40, 167, 69, 0.04)",
			hover: "rgba(0, 0, 0, 0.02)"
		}
	},
	warning: {
		main: "#FF6300", // Coquelicot from Figma (même que primary)
		light: "#FF8F70",
		dark: "#E65B2C"
	},
	error: {
		main: "#F44336",
		light: "#E57373",
		dark: "#D32F2F",
		alpha: {
			light: "rgba(255, 100, 100, 0.1)",
			lighter: "rgba(255, 100, 100, 0.05)"
		}
	},
	info: {
		main: "#4836bf", // Persian Blue (même que secondary pour cohérence)
		light: "#6B5CE5", // Version plus claire du Persian Blue
		dark: "#201E69", // Midnight Blue from Figma
		alpha: {
			light: "rgba(72, 54, 191, 0.05)"
		}
	},
	mission: {
		status: "#fcce03" // Yellow for mission status
	},
	diff: {
		added: {
			light: "rgba(100, 255, 100, 0.1)",
			lighter: "rgba(100, 255, 100, 0.05)"
		},
		removed: {
			light: "rgba(255, 100, 100, 0.1)",
			lighter: "rgba(255, 100, 100, 0.05)"
		}
	},
	proposal: {
		// Couleurs spécifiques des propositions from Figma
		dyke: "#3B2325", // Brun foncé
		gunmetal: "#242C35", // Bleu-gris foncé
		seashell: "#FDF4F2", // Rose très clair
		cornsilk: "#FFF8DC", // Jaune très clair
		aliceBlue: "#E5F6FF", // Bleu très clair
		magnolia: "#EFEEFA" // Violet clair
	},
	shadow: {
		light: "0 2px 8px rgba(0,0,0,0.08)",
		medium: "0px 2px 8px rgba(0, 0, 0, 0.05)"
	},
	/**
	 * Brand "V5.1 editorial" palette — used by the public anonymized-talent
	 * fiche and any standalone landing/marketing surface that follows the same
	 * charter. Deliberately distinct from `primary.main` (#FF6300) which is the
	 * in-app coquelicot — the public fiche uses a slightly punchier #FF4300 per
	 * the validated Figma. Always reference these constants instead of inlining
	 * hex codes in component sx props.
	 */
	brand: {
		coquelicot: "#FF4300", // Public fiche accent (V5.1 charter)
		midnight: "#281E69", // Public fiche background — alias of secondary.dark
		persian: "#4836bf" // Public fiche gradient stop — alias of secondary.main
	}
};

/**
 * Brand font stack — used by the public anonymized-talent landing and any other
 * standalone surface that follows the V5.1 editorial charter. The in-app theme
 * keeps the MUI defaults; this is for opt-in usage on public pages only.
 */
export const brandFontFamily = "Manrope, sans-serif";

// Create the theme
export const themeSettings = (): ThemeOptions => ({
	palette: {
		mode: "light",
		primary: zoullooColors.primary,
		secondary: zoullooColors.secondary,
		background: zoullooColors.background,
		text: zoullooColors.text,
		success: zoullooColors.success,
		warning: zoullooColors.warning,
		error: zoullooColors.error,
		info: zoullooColors.info
	},
	typography: {
		fontFamily: ["Manrope", "Roboto", "sans-serif"].join(","),
		h1: {
			fontFamily: "Manrope, sans-serif",
			fontSize: "2.5rem",
			fontWeight: 700,
			lineHeight: 1.2,
			letterSpacing: "-0.5px"
		},
		h2: {
			fontFamily: "Manrope, sans-serif",
			fontSize: "1.6rem",
			fontWeight: 700,
			lineHeight: 1.3,
			letterSpacing: "-0.5px"
		},
		h3: {
			fontFamily: "Manrope, sans-serif",
			fontSize: "1.3rem",
			fontWeight: 600,
			lineHeight: 1.4
		},
		h4: {
			fontFamily: "Manrope, sans-serif",
			fontSize: "1.2rem",
			fontWeight: 600,
			lineHeight: 1.4
		},
		h5: {
			fontFamily: "Manrope, sans-serif",
			fontSize: "1.1rem",
			fontWeight: 600,
			lineHeight: 1.5
		},
		h6: {
			fontFamily: "Manrope, sans-serif",
			fontSize: "1rem",
			fontWeight: 600,
			lineHeight: 1.6
		},
		body1: {
			fontFamily: "Manrope, sans-serif",
			fontSize: "1rem",
			lineHeight: 1.5
		},
		body2: {
			fontFamily: "Manrope, sans-serif",
			fontSize: "0.95rem",
			lineHeight: 1.57
		},
		button: {
			fontFamily: "Manrope, sans-serif",
			textTransform: "none",
			fontWeight: 500
		}
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					padding: "8px 24px",
					boxShadow: "none",
					"&:hover": {
						boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)"
					},
					height: "50px"
				}
			}
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
					border: `1px solid ${zoullooColors.grey[400]}`,
					borderRadius: 16
				},
				elevation0: {
					border: `1px solid ${zoullooColors.grey[400]}`
				},
				rounded: {
					borderRadius: 16
				}
			}
		},
		MuiCard: {
			styleOverrides: {
				root: {
					boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
					border: `1px solid ${zoullooColors.grey[400]}`,
					borderRadius: 16
				}
			}
		},
		MuiTableContainer: {
			styleOverrides: {
				root: {
					border: `1px solid ${zoullooColors.grey[400]}`,
					borderRadius: 16,
					overflow: "hidden",
					overflowX: "auto"
				}
			}
		},
		MuiTable: {
			styleOverrides: {
				root: {
					borderCollapse: "separate",
					borderSpacing: 0
				}
			}
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					"& .MuiTableRow-root": {
						backgroundColor: zoullooColors.background.paper
					}
				}
			}
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					padding: "16px 24px",
					borderBottom: `1px solid ${zoullooColors.grey[100]}`
				},
				head: {
					backgroundColor: zoullooColors.grey[100],
					color: "#000000",
					fontWeight: 600,
					fontSize: "0.875rem",
					textTransform: "uppercase",
					letterSpacing: "0.5px",
					padding: "16px 24px",
					borderBottom: "none",
					"&:first-of-type": {
						borderTopLeftRadius: 16
					},
					"&:last-of-type": {
						borderTopRightRadius: 16
					}
				},
				body: {
					color: zoullooColors.text.black,
					fontSize: "0.9375rem",
					padding: "20px 24px"
				}
			}
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					"&:hover": {
						backgroundColor: zoullooColors.grey[50]
					},
					"&:last-child td": {
						borderBottom: 0
					}
				}
			}
		},
		MuiTextField: {
			defaultProps: {
				color: "secondary",
				slotProps: {
					inputLabel: { sx: { fontSize: "1.2rem" }, size: "normal", shrink: true }
				}
			},
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						borderRadius: 8,
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							border: `1px solid ${zoullooColors.secondary.main} !important`
						},
						"&:hover fieldset": {
							border: `1px solid ${zoullooColors.secondary.main} !important`
						}
					},
					"& .MuiFormHelperText-root": {
						"&.Mui-error": {
							fontSize: "1rem"
						}
					}
				}
			}
		},
		MuiChip: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					fontWeight: 500
				}
			}
		},
		MuiAvatar: {
			styleOverrides: {
				root: {
					backgroundColor: zoullooColors.secondary.light,
					width: 32,
					height: 32,
					fontSize: "0.875rem"
				},
				// You can also add more specific overrides if needed
				colorDefault: {
					backgroundColor: zoullooColors.secondary.light,
					color: zoullooColors.secondary.contrastText
				}
			}
		},
		MuiPagination: {
			styleOverrides: {
				root: {
					"& .MuiPaginationItem-root": {
						borderRadius: 8,
						margin: "0 4px",
						fontWeight: 500,
						fontSize: "0.875rem",
						"&.Mui-selected": {
							backgroundColor: zoullooColors.primary.main,
							color: "#FFFFFF",
							boxShadow: "0 2px 8px rgba(255, 111, 61, 0.25)",
							"&:hover": {
								backgroundColor: zoullooColors.primary.dark
							}
						}
					},
					"& .MuiPaginationItem-ellipsis": {
						opacity: 0.6
					}
				}
			}
		},
		MuiAutocomplete: {
			styleOverrides: {
				paper: {
					"& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
						"&:hover": {
							backgroundColor: zoullooColors.secondary.main,
							color: zoullooColors.secondary.contrastText
						},
						"&[aria-selected='true']": {
							backgroundColor: `${zoullooColors.grey[500]} !important`,
							color: `${zoullooColors.secondary.contrastText} !important`
						}
					},
					"& .MuiAutocomplete-listbox .MuiAutocomplete-groupLabel": {
						fontSize: "1.2rem",
						fontWeight: "bold"
					}
				},
				root: {
					"& .MuiOutlinedInput-root": {
						"&:hover fieldset": {
							border: `1px solid ${zoullooColors.secondary.main} !important`
						}
					}
				}
			}
		},
		MuiAlert: {
			styleOverrides: {
				filled: {
					color: "#ffffff"
				}
			}
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					border: "2px solid #e0e0e0",
					borderRadius: "8px",
					padding: "16px 32px",
					minWidth: 180,
					textTransform: "none",
					color: zoullooColors.text.primary,
					fontSize: "16px",
					fontWeight: 500,
					backgroundColor: "white",
					transition: "all 0.2s ease",
					"&:not(:first-of-type)": {
						marginLeft: "8px"
					},
					"&.Mui-selected": {
						border: "2px solid #28a745",
						backgroundColor: "white",
						color: zoullooColors.text.primary,
						"&:hover": {
							backgroundColor: "rgba(40, 167, 69, 0.04)",
							border: "2px solid #28a745"
						}
					},
					"&:hover": {
						backgroundColor: "rgba(0, 0, 0, 0.02)",
						border: "2px solid #bdbdbd"
					}
				}
			}
		},
		MuiToggleButtonGroup: {
			styleOverrides: {
				root: {
					"& .MuiToggleButtonGroup-grouped": {
						margin: "0",
						border: "2px solid #e0e0e0",
						borderRadius: "8px !important",
						"&:not(:first-of-type)": {
							marginLeft: "16px",
							borderRadius: "8px !important"
						},
						"&:not(:last-of-type)": {
							borderRadius: "8px !important"
						},
						"&:first-of-type": {
							borderRadius: "8px !important"
						},
						"&:last-of-type": {
							borderRadius: "8px !important"
						},
						"&.Mui-selected": {
							border: "2px solid #28a745"
						}
					}
				}
			}
		},
		MuiLink: {
			styleOverrides: {
				root: {
					color: zoullooColors.secondary.main,
					textDecorationColor: zoullooColors.secondary.main
				}
			}
		},
		MuiSwitch: {
			defaultProps: {
				color: "secondary"
			}
		}
	}
});

// Create theme instance
export const theme = createTheme(themeSettings());
